import { Controller } from "@hotwired/stimulus"

/**
 * Batch Actions Controller
 * 
 * Manages batch selection and actions for list items (projects, variants, etc.).
 * Handles checkbox selection, visual feedback, and batch action visibility.
 * 
 * Features:
 * - Tracks selected items across the page
 * - Shows/hides batch action buttons based on selection count
 * - Updates action count displays
 * - Restores selection state after page reloads
 * - Handles both checkbox clicks and row clicks for selection
 * 
 * Usage: Add data-controller="batch-actions" to list containers with checkboxes and batch action buttons
 */
export default class extends Controller {
  static targets = ["listItem", "deleteButton", "noSelectionMessage", "selectAllCheckbox", "actionButton", "actionForm", "projectUuidInput", "uuidsContainer"]

  connect() {
    this.restoreSelection()
    this.updateActionCount()
    // If this is the move/clone aside, refresh button visibility (e.g. after Turbo loads the frame)
    if (this.hasActionButtonTarget) {
      const count = document.querySelectorAll('tr.list-item.selected').length
      const checkedRadio = this.element.querySelector('input[type="radio"]:checked')
      if (count > 0 && checkedRadio) {
        this.actionButtonTarget.style.display = 'inline-block'
        if (this.hasNoSelectionMessageTarget) this.noSelectionMessageTarget.style.display = 'none'
        if (this.hasProjectUuidInputTarget) this.projectUuidInputTarget.value = checkedRadio.value
        const label = checkedRadio.closest('tr').querySelector('label')
        const targetSpans = this.element.querySelectorAll('.move-actions-target-project, .clone-actions-target-project')
        const title = label ? label.textContent.trim() : 'Unnamed project'
        targetSpans.forEach(span => { span.textContent = title })
      }
    }
  }

  restoreSelection() {
    const checkboxes = this.element.querySelectorAll('input[type="checkbox"]:checked')
    checkboxes.forEach(checkbox => {
      const item = checkbox.closest('tr')
      if (item) item.classList.add('selected')
    })

    const selectedItems = this.element.querySelectorAll('tr.selected')
    selectedItems.forEach(item => {
      const checkbox = item.querySelector('input[type="checkbox"]')
      if (checkbox && !checkbox.checked) checkbox.checked = true
    })
  }

  selectItem(event) {
    // Don't select if the clicked element has prevent-deselect class (also check for legacy prevent-select)
    if (event.target.classList.contains('prevent-deselect') || event.target.classList.contains('prevent-select')) {
      return
    }

    const item = event.currentTarget
    const checkbox = item.querySelector('input[type="checkbox"]')

    if (checkbox) {
      checkbox.checked = !checkbox.checked
      // Create a new event-like object for the checkbox
      const checkboxEvent = {
        target: checkbox,
        stopPropagation: () => { }
      }
      this.selectCheckbox(checkboxEvent)
    }
  }

  selectCheckbox(event) {
    event.stopPropagation()
    const checkbox = event.target
    const item = checkbox.closest('tr')

    if (checkbox.checked) {
      item.classList.add('selected')
    } else {
      item.classList.remove('selected')
    }

    this.updateActionCount()
  }

  updateActionCount() {
    const selectedItems = document.querySelectorAll('tr.list-item.selected')
    const count = selectedItems.length

    // Update action count text
    const actionCountElements = document.querySelectorAll('.action-count')
    actionCountElements.forEach(element => {
      element.textContent = count
    })

    // Show/hide batch actions based on selection
    const batchActions = Array.from(document.querySelectorAll('.batch-action')).map(el => el.closest('.nav-item'))
    batchActions.forEach(el => {
      if (el && count === 0) {
        el.style.display = 'none'
      } else if (el) {
        el.style.display = 'block'
      }
    })

    // Show/hide single vs multiple messages
    const singleElements = document.querySelectorAll('.action-count-single')
    const multipleElements = document.querySelectorAll('.action-count-multiple')
    const noSelectionMessages = document.querySelectorAll('[data-batch-actions-target="noSelectionMessage"]')
    const deleteButtons = document.querySelectorAll('[data-batch-actions-target="deleteButton"]')
    const warningAlerts = document.querySelectorAll('.action-count-warning')

    if (count === 0) {
      // No selection - show "Please select one or more projects to delete"
      warningAlerts.forEach(el => el.style.display = 'none')
      noSelectionMessages.forEach(el => el.style.display = 'block')
      deleteButtons.forEach(el => el.style.display = 'none')
      this.updateMoveCloneButtonVisibility(0)
    } else if (count === 1) {
      // Single selection - show "Delete this project?" and "Are you sure you want to delete this project..."
      warningAlerts.forEach(el => el.style.display = 'block')
      singleElements.forEach(el => el.style.display = 'block')
      multipleElements.forEach(el => el.style.display = 'none')
      noSelectionMessages.forEach(el => el.style.display = 'none')
      deleteButtons.forEach(el => el.style.display = 'inline-block')
      this.updateMoveCloneButtonVisibility(1)
    } else {
      // Multiple selection - show "Delete X projects?" and "Are you sure you want to delete these X projects..."
      warningAlerts.forEach(el => el.style.display = 'block')
      singleElements.forEach(el => el.style.display = 'none')
      multipleElements.forEach(el => el.style.display = 'block')
      noSelectionMessages.forEach(el => el.style.display = 'none')
      deleteButtons.forEach(el => el.style.display = 'inline-block')
      this.updateMoveCloneButtonVisibility(count)
    }
  }

  updateMoveCloneButtonVisibility(selectedCount) {
    const actionButtons = document.querySelectorAll('[data-batch-actions-target="actionButton"]')
    actionButtons.forEach(button => {
      const card = button.closest('[data-controller*="batch-actions"]')
      if (!card) return
      const checkedRadio = card.querySelector('input[type="radio"]:checked')
      const noSelectionMsg = card.querySelector('[data-batch-actions-target="noSelectionMessage"]')
      if (selectedCount > 0 && checkedRadio) {
        button.style.display = 'inline-block'
        if (noSelectionMsg) noSelectionMsg.style.display = 'none'
      } else {
        button.style.display = 'none'
        if (noSelectionMsg) noSelectionMsg.style.display = 'block'
      }
    })
  }

  updateTargetProject(event) {
    const radio = event.target
    const projectUuid = radio.value
    const label = radio.closest('tr').querySelector('label')
    const projectTitle = label ? label.textContent.trim() : 'Unnamed project'

    if (this.hasProjectUuidInputTarget) {
      this.projectUuidInputTarget.value = projectUuid
    }
    const card = this.element
    const targetSpans = card.querySelectorAll('.move-actions-target-project, .clone-actions-target-project')
    targetSpans.forEach(span => { span.textContent = projectTitle })

    const selectedCount = document.querySelectorAll('tr.list-item.selected').length
    if (this.hasActionButtonTarget) {
      if (selectedCount > 0) {
        this.actionButtonTarget.style.display = 'inline-block'
        if (this.hasNoSelectionMessageTarget) {
          this.noSelectionMessageTarget.style.display = 'none'
        }
      }
    }
  }

  submitVariants(event) {
    const form = event.target
    const uuidsContainer = form.querySelector('[data-batch-actions-target="uuidsContainer"]')
    const projectUuidInput = form.querySelector('[data-batch-actions-target="projectUuidInput"]')
    if (!uuidsContainer || !projectUuidInput) return

    const selectedItems = document.querySelectorAll('tr.list-item.selected')
    const uuids = []
    selectedItems.forEach(tr => {
      const variantUuid = tr.dataset.uuid
      if (variantUuid) uuids.push(variantUuid)
    })

    if (uuids.length === 0) {
      event.preventDefault()
      return
    }

    uuidsContainer.innerHTML = ''
    uuids.forEach(uuid => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = 'uuids[]'
      input.value = uuid
      uuidsContainer.appendChild(input)
    })
  }

  deleteProjects(event) {
    event.preventDefault()

    const button = event.currentTarget
    const deleteUrl = button.dataset.deleteUrl
    if (!deleteUrl) {
      console.warn('[BatchActionsController] No delete URL provided for deleteProjects')
      return
    }

    // Collect selected project UUIDs
    const selectedItems = document.querySelectorAll('tr.list-item.selected')
    const uuids = []
    selectedItems.forEach(tr => {
      const uuid = tr.dataset.uuid
      const path = tr.dataset.path
      // Only include project rows (path === "/projects") or rows without an explicit path
      if (uuid && (!path || path === '/projects')) {
        uuids.push(uuid)
      }
    })

    if (uuids.length === 0) {
      // Nothing selected; updateActionCount already shows the appropriate message
      return
    }

    // Build a form and submit via POST to delete_projects_path
    const form = document.createElement('form')
    form.method = 'post'
    form.action = deleteUrl

    // CSRF token
    const csrf = document.querySelector('meta[name="csrf-token"]')
    if (csrf && csrf.content) {
      const tokenInput = document.createElement('input')
      tokenInput.type = 'hidden'
      tokenInput.name = 'authenticity_token'
      tokenInput.value = csrf.content
      form.appendChild(tokenInput)
    }

    uuids.forEach(uuid => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = 'uuids[]'
      input.value = uuid
      form.appendChild(input)
    })

    document.body.appendChild(form)
    form.submit()
  }

  deleteVariants(event) {
    event.preventDefault()

    const button = event.currentTarget
    const deleteUrl = button.dataset.deleteUrl
    if (!deleteUrl) {
      console.warn('[BatchActionsController] No delete URL provided for deleteVariants')
      return
    }

    // Collect selected variant UUIDs
    const selectedItems = document.querySelectorAll('tr.list-item.selected')
    const uuids = []
    selectedItems.forEach(tr => {
      const uuid = tr.dataset.uuid
      const path = tr.dataset.path
      // Only include variant rows (path === "/variants") or rows without an explicit path
      if (uuid && (!path || path === '/variants')) {
        uuids.push(uuid)
      }
    })

    if (uuids.length === 0) {
      // Nothing selected; updateActionCount already shows the appropriate message
      return
    }

    // Try to get project id for redirect
    let projectId = null
    const variantsTable = document.getElementById('variants_list')
    if (variantsTable && variantsTable.dataset.project_uuid) {
      projectId = variantsTable.dataset.project_uuid
    }

    // Build a form and submit via POST to delete_variants_variants_path
    const form = document.createElement('form')
    form.method = 'post'
    form.action = deleteUrl

    // CSRF token
    const csrf = document.querySelector('meta[name="csrf-token"]')
    if (csrf && csrf.content) {
      const tokenInput = document.createElement('input')
      tokenInput.type = 'hidden'
      tokenInput.name = 'authenticity_token'
      tokenInput.value = csrf.content
      form.appendChild(tokenInput)
    }

    uuids.forEach(uuid => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = 'uuids[]'
      input.value = uuid
      form.appendChild(input)
    })

    if (projectId) {
      const projectInput = document.createElement('input')
      projectInput.type = 'hidden'
      projectInput.name = 'project_id'
      projectInput.value = projectId
      form.appendChild(projectInput)
    }

    document.body.appendChild(form)
    form.submit()
  }

  navigateToDelete(event) {
    // Navigation is handled by turbo_frame and turbo_action
  }

  selectAll(event) {
    event.stopPropagation()
    const selectAllCheckbox = event.target
    const isChecked = selectAllCheckbox.checked

    // Find all checkboxes in the list (excluding the select-all checkbox)
    const checkboxes = this.element.querySelectorAll('tbody input[type="checkbox"]')

    checkboxes.forEach(checkbox => {
      checkbox.checked = isChecked
      const item = checkbox.closest('tr')
      if (item) {
        if (isChecked) {
          item.classList.add('selected')
        } else {
          item.classList.remove('selected')
        }
      }
    })

    this.updateActionCount()
  }
} 